import type { ColumnDef } from "@tanstack/react-table";
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import SelectFilterByYear from "components/company/v3/SelectFilterByYear";
import TablePagination from "components/ui/Table/TablePagination";
import classNames from "components/ui/classNames";
import type { InputMaybe } from "types/graphqlTypes";

interface CompanyCorporateActionsViewProps<T> {
  data: T[];
  columns: (
    | ColumnDef<T, string>
    | ColumnDef<T, number>
    | ColumnDef<T, boolean>
    | ColumnDef<T, Date>
    | ColumnDef<T, InputMaybe<string>>
    | ColumnDef<T, InputMaybe<number>>
    | ColumnDef<T, InputMaybe<Date>>
    | ColumnDef<T, InputMaybe<boolean>>
    | ColumnDef<T, InputMaybe<unknown>>
  )[];
  availableYears: number[];
  filterByYear?: number | null;
  setFilterByYear: (value: number | null) => void;
  isRenderPageDefault?: boolean;
}

export default function CompanyCorporateActionsView<T extends Record<string, unknown>>({
  data,
  columns,
  availableYears,
  filterByYear,
  setFilterByYear,
  isRenderPageDefault = false,
}: CompanyCorporateActionsViewProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: isRenderPageDefault ? 10 : 16,
      },
    },
  });

  return (
    <div className="flex h-full w-full flex-col items-end space-y-2">
      <div className="flex w-full items-start">
        <div className="w-full md:w-36">
          <SelectFilterByYear value={filterByYear ?? null} onChange={setFilterByYear} availableYears={availableYears} />
        </div>
      </div>

      <div className="relative h-full w-full">
        <div className="overflow-x-scroll pb-4 lg:w-full">
          <div
            className={classNames("relative overflow-auto rounded-xl border border-slate-200", "dark:border-slate-950")}
          >
            <table className="w-full divide-y divide-slate-200 dark:divide-slate-900">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                  >
                    {headerGroup.headers
                      .filter((header) => !header.column.columnDef.meta?.isHidden)
                      .map((header) => {
                        return (
                          <th
                            key={header.id}
                            className={classNames(
                              "m-0 bg-slate-50 p-0 text-center dark:bg-slate-800",
                              header.column.columnDef.meta?.header?.className,
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        );
                      })}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-800">
                      {row
                        .getVisibleCells()
                        .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                        .map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <TablePagination<T> table={table} />
    </div>
  );
}
